const REACT_APP_API_URL = process.env.REACT_APP_API_URL || "";

const getSessionId = () => {
  let id = localStorage.getItem("hg_session_id");
  if (!id) {
    id = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("hg_session_id", id);
  }
  return id;
};

// Strip hash and query params from URL, keep only base path for readability
const stripUrlParams = (url) => {
  try {
    return url.split("#")[0].split("?")[0];
  } catch (_) {
    return url;
  }
};

const getStripeIframeInfo = () => {
  try {
    const iframe =
      document.querySelector('iframe[src*="m.stripe.network"]') ||
      document.querySelector('iframe[name*="stripe"]') ||
      document.querySelector('iframe[src*="js.stripe.com"]');

    if (!iframe) return null;

    const src = iframe.src || "";
    const hash = src.split("#")[1] || "";
    const params = {};
    hash.split("&").forEach((part) => {
      const [key, val] = part.split("=");
      if (key) params[decodeURIComponent(key)] = decodeURIComponent(val || "");
    });

    return {
      iframe_src: stripUrlParams(src),
      stripe_muid: params["muid"] || null,
      stripe_sid: params["sid"] || null,
      stripe_version: params["version"] || null,
    };
  } catch (_) {
    return null;
  }
};

// Group stripe requests by base filename instead of sending 30+ duplicate long URLs
const getStripePerformanceSummary = () => {
  try {
    const entries = performance.getEntriesByType("resource").filter(
      (e) =>
        e.name.includes("m.stripe.network") ||
        e.name.includes("js.stripe.com")
    );

    const grouped = {};
    entries.forEach((e) => {
      const baseUrl = stripUrlParams(e.name);
      const filename = baseUrl.split("/").pop() || baseUrl;
      if (!grouped[filename]) {
        grouped[filename] = {
          filename,
          base_url: baseUrl,
          initiator_type: e.initiatorType,
          count: 0,
          max_duration_ms: 0,
          total_transfer_bytes: 0,
          any_non_zero_status: null,
        };
      }
      grouped[filename].count += 1;
      grouped[filename].max_duration_ms = Math.max(
        grouped[filename].max_duration_ms,
        Math.round(e.duration)
      );
      grouped[filename].total_transfer_bytes += e.transferSize || 0;
      if (e.responseStatus) {
        grouped[filename].any_non_zero_status = e.responseStatus;
      }
    });

    return Object.values(grouped);
  } catch (_) {
    return [];
  }
};

const getNetworkInfo = () => {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return null;
    return {
      effective_type: conn.effectiveType || null,
      downlink_mbps: conn.downlink || null,
      rtt_ms: conn.rtt || null,
      save_data: conn.saveData || false,
    };
  } catch (_) {
    return null;
  }
};

export const logStripeError = (event, component, bookingSlug = null) => {
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);

  const payload = {
    session_id: getSessionId(),
    component,
    booking_slug: bookingSlug,
    page_path: window.location.pathname,
    page_url: window.location.href,

    error_type: event?.error?.type || "unknown",
    error_code: event?.error?.code || null,
    error_message: event?.error?.message || "unknown",
    stripe_element_type: event?.elementType || null,

    stripe_iframe: getStripeIframeInfo(),
    stripe_requests: getStripePerformanceSummary(),

    network_info: getNetworkInfo(),

    user_agent: ua,
    is_ios: isIos,
    is_safari: isSafari,
    suspected_ios_issue: isIos && isSafari,

    page_visibility: document.visibilityState || null,
    timestamp: new Date().toISOString(),
  };

  localStorage.setItem("stripe_error_before_reload", JSON.stringify(payload));

  fetch(`${REACT_APP_API_URL}/log-stripe-error`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => {});
};

export const checkStripeErrorAfterReload = () => {
  const stored = localStorage.getItem("stripe_error_before_reload");
  if (!stored) return;

  try {
    const previousError = JSON.parse(stored);
    const followUpPayload = {
      ...previousError,
      reload_confirmed: true,
      reload_timestamp: new Date().toISOString(),
    };

    fetch(`${REACT_APP_API_URL}/log-stripe-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(followUpPayload),
    }).catch(() => {});
  } catch (_) {}

  localStorage.removeItem("stripe_error_before_reload");
};
